import React, { useEffect, useRef, ReactNode } from 'react';
import { useShop } from './ShopProvider';
import { CartStep } from './CartProvider';
import { eventEmitter, CartStepChangePayload } from '@/lib/eventEmitter';
import { heartbeatVisitorId } from '@/lib/cdpVisitor';

const STORAGE_KEY = 'heartbeat_session_id';

const getOrCreateSessionId = (): string => {
    const existing = localStorage.getItem(STORAGE_KEY);
    if (existing) return existing;
    const id = crypto.randomUUID();
    localStorage.setItem(STORAGE_KEY, id);
    return id;
};

const SESSION_ID = getOrCreateSessionId();

const HEARTBEAT_INTERVAL = 30_000;

const initialTracking = (() => {
    const params = new URLSearchParams(window.location.search);
    return {
        referrer: document.referrer || undefined,
        utmSource: params.get('utm_source') || undefined,
        utmMedium: params.get('utm_medium') || undefined,
        utmCampaign: params.get('utm_campaign') || undefined,
    };
})();

const cartStepToPage: Record<string, string> = {
    [CartStep.Tickets]: 'products',
    [CartStep.Order]: 'details',
    [CartStep.Checkout]: 'payment',
    [CartStep.PaymentReturn]: 'confirmation',
};

export const HeartbeatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { shopSlug, baseUrl } = useShop();
    const currentPageRef = useRef('products');

    useEffect(() => {
        const handleStepChange = ({ step }: CartStepChangePayload) => {
            currentPageRef.current = cartStepToPage[step] ?? 'products';
            console.debug('[Heartbeat] Cart step changed:', step, '→ page:', currentPageRef.current);
        };
        eventEmitter.on('CART_STEP_CHANGE', handleStepChange);
        return () => { eventEmitter.off('CART_STEP_CHANGE', handleStepChange); };
    }, []);

    useEffect(() => {
        console.debug('[Heartbeat] Provider mounted. shopSlug:', shopSlug, 'baseUrl:', baseUrl);
        console.debug('[Heartbeat] Session ID:', SESSION_ID);
        console.debug('[Heartbeat] Tracking:', initialTracking);

        const sendHeartbeat = () => {
            if (document.visibilityState === 'hidden') {
                console.debug('[Heartbeat] Tab hidden, skipping');
                return;
            }

            const page = currentPageRef.current;
            const url = `${baseUrl}/shop/${shopSlug}/heartbeat`;
            const visitorId = heartbeatVisitorId();
            const body = {
                sessionId: SESSION_ID,
                ...(visitorId ? { visitorId } : {}),
                page,
                ...initialTracking,
            };

            console.debug('[Heartbeat] Sending →', url, body);

            fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            })
                .then((res) => {
                    console.debug('[Heartbeat] Response:', res.status);
                })
                .catch((err) => {
                    console.debug('[Heartbeat] Error:', err);
                });
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                console.debug('[Heartbeat] Tab became visible, sending immediately');
                sendHeartbeat();
            } else {
                console.debug('[Heartbeat] Tab hidden, pausing');
            }
        };

        sendHeartbeat();
        const interval = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            console.debug('[Heartbeat] Provider unmounting, clearing interval');
            clearInterval(interval);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [baseUrl, shopSlug]);

    return <>{children}</>;
};
